import type { PlaybookTransitionEvent } from '../hooks/use-conversation-events';

/**
 * PlaybookTransitionIndicator — Fatia 11.2.
 *
 * Banner discreto sticky abaixo do ConversationHeader quando transition de
 * playbook silenciosa acontece em modo H_c (policy `automatic_silent`).
 *
 * Decisão Tech Lead Q3 (25/Mai/2026): banner sticky discreto + auto-dismiss
 * 8s (não-intrusivo). Audit silent em qualquer pacote. Visual canonical
 * Brief 2A V2.3 §C (sub-banner sticky abaixo do header).
 *
 * Caller fornece `transition` populada via `useConversationEvents.lastTransition`
 * + `onDismiss` para fechar manualmente antes dos 8s. Quando `transition=null`,
 * componente retorna null.
 */
export function PlaybookTransitionIndicator({
  transition,
  onDismiss,
}: {
  transition: PlaybookTransitionEvent | null;
  onDismiss: () => void;
}): JSX.Element | null {
  if (transition === null) return null;

  return (
    <div
      className="flex items-center gap-3 border-b px-6 py-2"
      style={{
        borderColor: 'var(--info-border)',
        background: 'var(--info-bg)',
        color: 'var(--info-fg)',
      }}
      role="status"
      aria-live="polite"
      data-testid="playbook-transition-indicator"
    >
      <span aria-hidden="true" className="text-base">
        ↻
      </span>
      <div className="flex-1 text-sm">
        <span className="font-medium">Playbook trocado:</span>{' '}
        <span className="opacity-80">{transition.fromPlaybookId ?? '(sem playbook anterior)'}</span>{' '}
        →{' '}
        <span className="font-semibold" data-testid="playbook-transition-to">
          {transition.toPlaybookName}
        </span>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        className="rounded px-2 py-1 text-xs transition-opacity hover:opacity-60"
        aria-label="Fechar indicador de transição"
        data-testid="playbook-transition-dismiss"
      >
        ✕
      </button>
    </div>
  );
}
